import { Typography } from '@mui/material';
import { Alert, type AlertProps, Show } from '@side/pantry';
import type { ReactNode } from 'react';
import { ErrorCode, type FileRejection } from 'react-dropzone';
import { getFileNamesFromRejections } from './helpers';

interface Props extends AlertProps {
  rejections: FileRejection[];
  supportedFileTypes: ReactNode;
  maxFileSize: string;
  remainingFiles?: number;
}

export const FileErrorAlert = ({
  maxFileSize,
  rejections,
  remainingFiles,
  supportedFileTypes,
  ...rest
}: Props) => {
  if (!rejections?.length) return null;

  const errorCodes = new Set(
    rejections.flatMap((rejection) =>
      rejection.errors.map((error) => error.code),
    ),
  );
  const hasTooManyFilesError = errorCodes.has(ErrorCode.TooManyFiles);
  const fileSizeError = errorCodes.has(ErrorCode.FileTooLarge);
  const fileTypeError = errorCodes.has(ErrorCode.FileInvalidType);

  let message: ReactNode = 'There was an error uploading your document';
  let fileList: string[] = [];

  if (hasTooManyFilesError && remainingFiles) {
    message = `The number of files uploaded exceeds the limit. You can still upload up to ${remainingFiles} file${remainingFiles > 1 ? 's' : ''}.`;
  } else if (fileSizeError) {
    fileList = getFileNamesFromRejections(rejections, ErrorCode.FileTooLarge);
    message = (
      <>
        Upload failed. The file sizes must be&nbsp;
        <strong>less than {maxFileSize}</strong>, and the following files were
        too large:
      </>
    );
  } else if (fileTypeError) {
    fileList = getFileNamesFromRejections(
      rejections,
      ErrorCode.FileInvalidType,
    );
    message = (
      <>
        Upload failed. The file type must be a&nbsp;
        <strong>{supportedFileTypes}</strong>. The following files were invalid:
      </>
    );
  }

  return (
    <Alert severity="error" {...rest}>
      <Typography variant="body2" sx={{ pb: 0.5, pr: 2 }}>
        {message}
      </Typography>
      <Show when={fileList.length}>
        <ul style={{ paddingInlineStart: '24px', margin: 0 }}>
          {fileList.map((fileName) => (
            <li key={fileName}>
              <Typography variant="body2">{fileName}</Typography>
            </li>
          ))}
        </ul>
      </Show>
    </Alert>
  );
};
